import type { ReactNode } from 'react'

import appStoreBadge from '@/assets/marketing/fuelapp/apple-store-light.svg'
import fuelCodeInHand from '@/assets/marketing/fuelapp/fuel-code-in-hand.webp'
import googlePlayBadge from '@/assets/marketing/fuelapp/google-play-light.svg'
import bookkeeperProfitAndLoss from '@/assets/marketing/images/bookkeeper-profit-and-loss.png'
import doubleTireTread from '@/assets/marketing/images/double-tire-tread.svg'
import { Container } from '@/components/Container'
import { Badge } from '@/components/ui/badge'
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Link } from '@/components/ui/Link'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { PremiumFinancingBanner } from './PremiumFinancingBanner'

interface Product {
  name: string
  href?: string
  description: ReactNode
  image?: { src: string; alt: string; className: string }
  /** Price tag in the card's top-left corner. */
  badge?: string
  /** Store badges in place of the Learn more link. */
  storeBadges?: boolean
  source?: { label: string; href: string }
  tone?: Tone
  wide?: boolean
  /** Lead card: larger copy, bottom-aligned (centered on mobile), no title. */
  featured?: boolean
}

enum Tone {
  Ink = 'ink',
  Brand = 'brand',
  BrandLight = 'brand-light',
}

const TONE_CARD: Record<Tone, string> = {
  [Tone.Ink]: 'bg-ink-900 text-white',
  [Tone.Brand]: 'bg-brand-900 border-brand-900 text-white',
  [Tone.BrandLight]: 'bg-brand-100',
}

const TONE_LINK: Record<Tone, string> = {
  [Tone.Ink]: 'text-white hover:text-white/80',
  [Tone.Brand]: 'text-white hover:text-white/80',
  [Tone.BrandLight]: 'text-brand-900 hover:text-brand-800',
}

/** Image fades end in the card's own ground. */
const TONE_FADE: Record<Tone, string> = {
  [Tone.Ink]: 'to-ink-900',
  [Tone.Brand]: 'to-brand-900',
  [Tone.BrandLight]: 'to-brand-100',
}

const PRODUCTS: Product[] = [
  {
    name: 'About Cai',
    description: (
      <>
        <p>
          Fight the fastest-rising costs in trucking this year: <br className="max-md:hidden" />
          <strong className="font-bold">diesel and insurance</strong>.
        </p>
        <p className="mt-3">
          Track every dollar with{' '}
          <a
            href="https://www.cai.ai/bookkeeper"
            className="text-brand-500 hover:text-brand-400 font-semibold"
          >
            Bookkeeper
          </a>
          , spend less at the pump with{' '}
          <a href="/fuelapp" className="text-brand-500 hover:text-brand-400 font-semibold">
            FuelApp
          </a>
          , and grow faster with{' '}
          <a href="/insurance" className="text-brand-500 hover:text-brand-400 font-semibold">
            Insurance
          </a>{' '}
          servicing that doesn’t hold your fleet back.
        </p>
      </>
    ),
    source: {
      label: 'Source: ATRI, Operational Costs of Trucking 2026 Update',
      href: 'https://truckingresearch.org/2026/07/analysis-of-the-operational-costs-of-trucking-2026-update/',
    },
    tone: Tone.Brand,
    wide: true,
    featured: true,
  },
  {
    name: 'Bookkeeper',
    href: 'https://www.cai.ai/bookkeeper',
    badge: '3 month free trial',
    tone: Tone.BrandLight,
    image: {
      src: bookkeeperProfitAndLoss,
      alt: 'A Bookkeeper profit and loss report with a bookkeeping task to review',
      className: 'object-contain',
    },
    description: 'Books kept current and closed within 5 days, plus a live bookkeeper',
  },
  {
    name: 'FuelApp',
    href: '/fuelapp',
    badge: 'Free',
    description: 'Diesel discounts at top truck stop brands nationwide',
    image: {
      src: fuelCodeInHand,
      alt: 'A FuelApp fuel code on a phone at a Dove’s truck stop',
      className: 'object-cover object-[50%_65%]',
    },
    storeBadges: true,
    tone: Tone.Ink,
  },
]

const STORE_BADGES = [
  {
    href: 'https://apps.apple.com/us/app/fuelapp-diesel-discounts/id6759668944',
    src: appStoreBadge,
    alt: 'Download on the App Store',
    grow: 'flex-[120_1_0%]',
  },
  {
    href: 'https://play.google.com/store/apps/details?id=com.cloudtrucks.fuelapp&hl=en_US&pli=1',
    src: googlePlayBadge,
    alt: 'Get it on Google Play',
    grow: 'flex-[135_1_0%]',
  },
]

/** Full-width band below the quotes: premium financing, About Cai, and the Bookkeeper and FuelApp cross-sell. */
export function OtherProductsSection() {
  return (
    <section className="bg-ink-100 mt-12 py-12">
      <Container>
        <PremiumFinancingBanner />
        <Separator className="my-12" />
        <div className="grid gap-4 max-md:auto-rows-fr md:grid-cols-2 lg:grid-cols-4">
          {PRODUCTS.map(
            ({ name, href, description, image, storeBadges, source, tone, wide, featured, badge }) => (
              <Card
                key={name}
                className={cn(
                  'relative overflow-hidden rounded-xl',
                  wide && 'md:col-span-2',
                  tone && TONE_CARD[tone]
                )}
              >
                {featured && (
                  <div
                    aria-hidden="true"
                    className="bg-brand-800/10 pointer-events-none absolute inset-0 mask-size-[auto_140%] mask-right mask-no-repeat"
                    style={{
                      maskImage: `url(${doubleTireTread})`,
                      WebkitMaskImage: `url(${doubleTireTread})`,
                    }}
                  />
                )}
                {image && (
                  <div className="relative -mt-6">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className={cn('block aspect-[4/3] w-full', image.className)}
                    />
                    <div
                      aria-hidden="true"
                      className={cn(
                        'absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-b from-transparent',
                        tone ? TONE_FADE[tone] : 'to-card'
                      )}
                    />
                    {badge && <Badge className="absolute top-4 left-4">{badge}</Badge>}
                  </div>
                )}
                <CardHeader className={cn(featured && 'relative mt-auto max-md:mb-auto')}>
                  {badge && !image && <Badge className="mb-2">{badge}</Badge>}
                  {featured ? (
                    <h2 className="mb-2 font-mono text-xs font-bold tracking-[0.14em] text-white/70 uppercase">
                      Save with Cai
                    </h2>
                  ) : (
                    <CardTitle>{name}</CardTitle>
                  )}
                  <CardDescription
                    className={cn(
                      tone === Tone.Ink && 'text-white/70',
                      tone === Tone.Brand && 'text-white',
                      featured && 'max-w-[44ch] text-xl leading-snug'
                    )}
                  >
                    {description}
                  </CardDescription>
                </CardHeader>
                <CardFooter className={cn('flex-col items-start gap-4', featured ? 'relative' : 'mt-auto')}>
                  {storeBadges ? (
                    // Flex weights match the badges' widths so both render at the same height.
                    <div className="flex w-full max-w-[263px] gap-2">
                      {STORE_BADGES.map((storeBadge) => (
                        <a
                          key={storeBadge.href}
                          href={storeBadge.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn('min-w-0', storeBadge.grow)}
                        >
                          <img src={storeBadge.src} alt={storeBadge.alt} className="block h-auto w-full" />
                        </a>
                      ))}
                    </div>
                  ) : (
                    href && (
                      <Link
                        kind="util"
                        emphasis={!tone}
                        href={href}
                        aria-label={`Learn more about ${name}`}
                        className={cn(tone && TONE_LINK[tone])}
                      >
                        Learn more
                      </Link>
                    )
                  )}
                  {source && (
                    <a
                      href={source.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-white/70 underline hover:text-white"
                    >
                      {source.label}
                    </a>
                  )}
                </CardFooter>
              </Card>
            )
          )}
        </div>
      </Container>
    </section>
  )
}
